import React, { useState, useEffect, useMemo } from "react";

import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { fetchVisits, updateVisitStatus } from "../api/api";

const PURPOSE_LABELS = {
  farm_visit: "Farm visit",
  collect_harvest: "Collect Harvest",
  construction_review: "Construction Review",
  meet_resident: "Meet resident",
  visit_resort: "Visit resort",
};

const STATUS_STYLES = {
  pending: { background: "#fef3c7", color: "#92400e" },
  confirmed: { background: "#dcfce7", color: "#166534" },
  cancelled: { background: "#fee2e2", color: "#991b1b" },
  rescheduled: { background: "#dbeafe", color: "#1e40af" },
  completed: { background: "#e5e7eb", color: "#374151" },
};

const FILTERS = [
  { label: "Pending approval", value: "pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "All", value: "all" },
];

function purposeLabel(purpose) {
  return PURPOSE_LABELS[purpose] || purpose;
}

function errorMessage(err) {
  const detail = err.response?.data?.detail;
  return detail || "Something went wrong. Please try again.";
}

function Visits() {

  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filter, setFilter] = useState("pending");
  const [selectedId, setSelectedId] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);
  const [actionError, setActionError] = useState("");

  const loadVisits = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchVisits();
      setVisits(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVisits();
  }, []);

  const filterCounts = useMemo(() => {
    const counts = { all: visits.length };
    for (const f of FILTERS) {
      if (f.value === "all") continue;
      counts[f.value] = visits.filter((v) => v.status === f.value).length;
    }
    return counts;
  }, [visits]);

  const filteredVisits = useMemo(() => {
    const sorted = [...visits].sort((a, b) => a.visit_date.localeCompare(b.visit_date));
    if (filter === "all") return sorted;
    return sorted.filter((v) => v.status === filter);
  }, [visits, filter]);

  const selectedVisit = visits.find((v) => v.id === selectedId) || null;

  const handleUpdateStatus = async (visitId, status) => {
    setRowBusy(visitId);
    setActionError("");
    try {
      const updated = await updateVisitStatus(visitId, status);
      setVisits((prev) => prev.map((v) => (v.id === visitId ? updated : v)));
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setRowBusy(null);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <h1 style={{ marginTop: 0, marginBottom: "20px" }}>Visit Management</h1>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}
        {actionError && <p className="login-error" style={{ color: "red" }}>{actionError}</p>}

        <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              className={filter === f.value ? "login-btn" : "register-btn"}
              style={{ width: "auto", padding: "8px 16px" }}
              onClick={() => setFilter(f.value)}
            >
              {f.label} ({filterCounts[f.value] ?? 0})
            </button>
          ))}
        </div>

        {loading && <p>Loading visits…</p>}

        {!loading && (
          <div style={{ background: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>

            <table className="plot-table" style={{ width: "100%", borderCollapse: "collapse" }}>

              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th>Customer</th>
                  <th>Plot</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Guests</th>
                  <th>Purpose</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>

                {filteredVisits.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: "15px" }}>No visits in this filter.</td></tr>
                )}

                {filteredVisits.map((visit) => (
                  <tr key={visit.id}>
                    <td>{visit.owner_name || "—"}</td>
                    <td>{visit.plot_number || "—"}</td>
                    <td>{visit.visit_date}</td>
                    <td>{visit.start_time} – {visit.end_time}</td>
                    <td>{visit.guest_count}</td>
                    <td>{purposeLabel(visit.purpose)}</td>
                    <td>
                      <span
                        style={{
                          ...(STATUS_STYLES[visit.status] || {}),
                          padding: "5px 12px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: 600,
                        }}
                      >
                        {visit.status}
                      </span>
                    </td>
                    <td>
                      <button className="view-btn" onClick={() => setSelectedId(visit.id)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}

              </tbody>

            </table>

          </div>
        )}

        {selectedVisit && (
          <div className="section-card" style={{ marginTop: "20px" }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>
                Visit — {selectedVisit.owner_name || "—"} — {selectedVisit.plot_number || "—"}
              </h3>
              <button className="view-btn" onClick={() => setSelectedId(null)}>Close</button>
            </div>

            <p>
              {selectedVisit.visit_date} · {selectedVisit.start_time} – {selectedVisit.end_time} · {selectedVisit.guest_count} guest(s) · Purpose: {purposeLabel(selectedVisit.purpose)}
            </p>

            {selectedVisit.notes && <p>Notes: {selectedVisit.notes}</p>}

            {selectedVisit.confirmation_number && (
              <p><strong>Confirmation number:</strong> {selectedVisit.confirmation_number}</p>
            )}

            {selectedVisit.status === "pending" ? (
              <div style={{ marginTop: "20px", display: "flex", gap: "15px" }}>
                <button
                  className="login-btn"
                  style={{ width: "auto", padding: "10px 20px" }}
                  onClick={() => handleUpdateStatus(selectedVisit.id, "confirmed")}
                  disabled={rowBusy === selectedVisit.id}
                >
                  {rowBusy === selectedVisit.id ? "…" : "Confirm visit"}
                </button>

                <button
                  style={{ width: "auto", padding: "10px 20px", background: "#dc2626", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
                  onClick={() => handleUpdateStatus(selectedVisit.id, "cancelled")}
                  disabled={rowBusy === selectedVisit.id}
                >
                  {rowBusy === selectedVisit.id ? "…" : "Cancel"}
                </button>
              </div>
            ) : (
              <p style={{ color: "#666", marginTop: "20px" }}>
                This visit is {selectedVisit.status} — no further action available.
              </p>
            )}

          </div>
        )}

      </div>

    </div>

  );

}

export default Visits;
